import { useAuthInfo } from '@propelauth/react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { addFactToVisualisation } from '@/new/pages/matters/:matterId/subsets/:subsetId/chronologies/components/visual-timeline/syncing-helpers/syncVisualisationWithTimelineChanges';
import { Classification, Relevance, ChronologyFact } from '@/types';

export const useCreateChronologyFactWithSync = (
  matterId: string,
  subsetId: string,
  chronologyId: string,
  docId: string,
) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();
  const authInfo = useAuthInfo();

  return useMutation(
    async ({
      description,
      factDateText,
      significance,
      classification,
      relevance,
    }: {
      description: string;
      factDateText: string;
      significance: string;
      classification: Classification;
      relevance: Relevance;
    }) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          description,
          factDateText,
          significance,
          classification,
          relevance,
        },
      });

      const response = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/${chronologyId}/doc/${docId}/add-fact`,
        fetchConfig,
      );

      if (!response.ok) {
        toast.error('Failed to create new fact');
        throw new Error('Failed to create new fact');
      }

      const newFact = await response.json();
      toast.success('New fact created');

      return newFact;
    },
    {
      onSuccess: async (newFact: ChronologyFact) => {
        // Invalidate chronology query first
        queryClient.invalidateQueries([QueryEntity.Chronology, { chronologyId }]);
        queryClient.invalidateQueries([QueryEntity.ChronologyFacts, { chronologyId }]);

        // Sync with visual timeline
        try {
          await addFactToVisualisation({
            matterId,
            subsetId,
            chronologyId,
            newFact,
            getAccessToken: authInfo.tokens.getAccessToken,
          });

          queryClient.invalidateQueries([QueryEntity.ChronologyVisualisation, { subsetId, chronologyId }]);
        } catch (error) {
          console.error('Failed to sync new fact with visual timeline:', error);
        }
      },
      onError: (error) => {
        console.error('Fetch Error: ', error);
        toast.error('Failed to create new fact');
      },
    },
  );
};
